import { Controller } from "@hotwired/stimulus"

/**
 * Filter Editor Controller
 *
 * Provides a dynamic, tag-based UI for editing placement matching filters.
 * Supports adding/removing filter values with a sleek dropdown + badge interface.
 */
export default class extends Controller {
  static targets = [
    "filterGroup",
    "selectedTags",
    "dropdown",
    "hiddenInputs",
    "distanceInput"
  ]

  static values = {
    filters: Object
  }

  connect() {
    // Tags are already rendered server-side in the partial,
    // so we don't need to call renderAllFilters() here
  }

  // Toggle dropdown visibility
  toggleDropdown(event) {
    const group = event.currentTarget.closest('[data-filter-group]')
    const dropdown = group.querySelector('[data-filter-editor-target="dropdown"]')
    dropdown.classList.toggle('hidden')
  }

  // Add a filter value
  addFilter(event) {
    event.preventDefault()
    const button = event.currentTarget
    const value = button.dataset.value
    const filterType = button.dataset.filterType
    const group = button.closest('[data-filter-group]')

    // Add the tag
    this.addTag(group, filterType, value)

    // Hide this option
    button.classList.add('hidden')

    // Close dropdown
    const dropdown = group.querySelector('[data-filter-editor-target="dropdown"]')
    dropdown.classList.add('hidden')

    // Update hidden inputs
    this.updateHiddenInputs(group, filterType)
  }

  // Remove a filter value
  removeFilter(event) {
    event.preventDefault()
    const button = event.currentTarget
    const tag = button.closest('[data-filter-tag]')
    const value = tag.dataset.value
    const filterType = tag.dataset.filterType
    const group = tag.closest('[data-filter-group]')

    // Remove the tag
    tag.remove()

    // Show the option again in dropdown
    const option = group.querySelector(`[data-filter-editor-target="dropdown"] [data-value="${CSS.escape(value)}"]`)
    if (option) {
      option.classList.remove('hidden')
    }

    // Update hidden inputs
    this.updateHiddenInputs(group, filterType)
  }

  // Toggle a boolean filter (like "Accepts Pets")
  toggleBoolean(event) {
    const checkbox = event.currentTarget
    const filterType = checkbox.dataset.filterType
    const filterKey = checkbox.dataset.filterKey

    // The hidden input is updated automatically by the checkbox
  }

  // Add a tag to the selected tags container
  addTag(group, filterType, value) {
    const tagsContainer = group.querySelector('[data-filter-editor-target="selectedTags"]')

    const tag = document.createElement('span')
    tag.className = 'inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-sm'
    tag.dataset.filterTag = ''
    tag.dataset.value = value
    tag.dataset.filterType = filterType
    tag.innerHTML = `
      ${this.escapeHtml(value)}
      <button type="button"
              class="hover:bg-primary/20 rounded-full p-0.5"
              data-action="click->filter-editor#removeFilter">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `
    tagsContainer.appendChild(tag)
  }

  // Update hidden inputs for form submission
  updateHiddenInputs(group, filterType) {
    const hiddenContainer = group.querySelector('[data-filter-editor-target="hiddenInputs"]')
    const tags = group.querySelectorAll('[data-filter-tag]')

    // Clear existing hidden inputs
    hiddenContainer.innerHTML = ''

    // Create hidden input for each selected value
    tags.forEach(tag => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `placements_plan[matching_filters][${filterType}][]`
      input.value = tag.dataset.value
      hiddenContainer.appendChild(input)
    })

    // If no values selected, add empty array marker
    if (tags.length === 0) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `placements_plan[matching_filters][${filterType}][]`
      input.value = ''
      hiddenContainer.appendChild(input)
    }
  }

  // Initialize filters from existing values on page load
  renderAllFilters() {
    const filters = this.filtersValue || {}

    this.filterGroupTargets.forEach(group => {
      const filterType = group.dataset.filterGroup
      const values = filters[filterType] || []

      values.forEach(value => {
        if (value) {
          this.addTag(group, filterType, value)
          // Hide the option in dropdown
          const option = group.querySelector(`[data-filter-editor-target="dropdown"] [data-value="${CSS.escape(value)}"]`)
          if (option) {
            option.classList.remove('hidden')
          }
        }
      })
    })
  }

  // Close dropdown when clicking outside
  closeDropdowns(event) {
    if (!event.target.closest('[data-filter-group]')) {
      this.dropdownTargets.forEach(dropdown => {
        dropdown.classList.add('hidden')
      })
    }
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
